import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Sheet, SheetContent, SheetTitle } from '../primitives/sheet';
import { useMediaQuery } from './context-panel';

/** Tailwind's `lg`: from here the details sit beside the list */
const WIDE_QUERY = '(min-width: 64rem)';

/** Space left below its top edge (minus the page's bottom padding), kept up to date when the window resizes */
function useFillHeight(active: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState<number>();
  useLayoutEffect(() => {
    if (!active) return;
    const measure = () => {
      const top = ref.current?.getBoundingClientRect().top ?? 0;
      setValue(Math.max(window.innerHeight - top - window.scrollY - 24, 0));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [active]);
  return { ref, value };
}

/** True from `lg`, where MasterDetail shows list and details side by side */
export function useMasterDetailWide() {
  return useMediaQuery(WIDE_QUERY);
}

interface MasterDetailProps {
  /** Filters, the list and its pagination */
  list: ReactNode;
  /** The selected item in full; null when nothing is selected */
  detail: ReactNode | null;
  /** Shown beside the list from `lg` while nothing is selected */
  placeholder?: ReactNode;
  /** Below `lg`: the details open over the list while this is true */
  open: boolean;
  /** Below `lg`: the details were closed (Escape, the close button or the backdrop) */
  onClose: () => void;
  labels: {
    /** Accessible name of the details panel and the sheet title */
    details: string;
  };
  /** A thin line between the list and the details */
  divided?: boolean;
  className?: string;
}

/**
 * A list with the selected item's full details next to it, so people never leave the list to read one.
 * From `lg` the details sit beside the list and scroll on their own; on phones they slide over the list.
 */
export function MasterDetail({
  list,
  detail,
  placeholder,
  open,
  onClose,
  labels,
  divided,
  className,
}: MasterDetailProps) {
  const wide = useMasterDetailWide();
  const height = useFillHeight(wide);

  if (wide) {
    // Fills the screen below its top edge; the list and the details each scroll on their own, so the page itself
    // stays still and nothing scrolls out from under the other column
    return (
      <div
        ref={height.ref}
        style={{ height: height.value }}
        className={cn(
          divided ? 'gap-6' : 'gap-4',
          'grid min-h-[28rem] lg:grid-cols-[minmax(20rem,24rem)_minmax(0,1fr)] 2xl:grid-cols-[27rem_minmax(0,1fr)]',
          className,
        )}
      >
        <div
          className={cn(
            '-m-1 grid min-h-0 min-w-0 content-start gap-3 overflow-x-hidden overflow-y-auto p-1 pr-2 [scrollbar-gutter:stable]',
            divided && 'border-r border-border pr-5',
          )}
        >
          {list}
        </div>
        <aside
          aria-label={labels.details}
          className="-m-1 grid min-h-0 min-w-0 content-start gap-3 overflow-y-auto overscroll-contain p-1"
        >
          {detail ?? placeholder}
        </aside>
      </div>
    );
  }

  return (
    <>
      <div className={cn('grid min-w-0 gap-3', className)}>{list}</div>
      <Sheet open={open && detail !== null} onOpenChange={(next) => !next && onClose()}>
        <SheetContent side="right" className="max-w-full gap-3 bg-background p-4 pt-12 sm:max-w-lg">
          <SheetTitle className="sr-only">{labels.details}</SheetTitle>
          {detail}
        </SheetContent>
      </Sheet>
    </>
  );
}
